import React, { useEffect, useId, useRef, useState } from 'react';
import { Info } from 'lucide-react';
import './InfoTooltip.css';

/**
 * Small ⓘ button next to a section heading. Opens on hover, keyboard focus
 * or click; closes on Escape, outside click or when hover/focus leaves.
 */
const InfoTooltip = ({ label, title, children }) => {
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const rootRef = useRef(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setPinned(false);
      }
    };
    const onPointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setPinned(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  const show = () => setOpen(true);
  const hide = () => {
    if (!pinned) setOpen(false);
  };

  return (
    <span
      ref={rootRef}
      className="info-tooltip"
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <button
        type="button"
        className="info-tooltip__trigger"
        aria-label={label}
        aria-expanded={open}
        aria-describedby={open ? panelId : undefined}
        onFocus={show}
        onBlur={hide}
        onClick={() => {
          setPinned((value) => !value);
          setOpen(true);
        }}
      >
        <Info size={15} strokeWidth={2} aria-hidden="true" />
      </button>
      {open ? (
        <span id={panelId} role="tooltip" className="info-tooltip__panel">
          {title ? <span className="info-tooltip__title">{title}</span> : null}
          <span className="info-tooltip__body">{children}</span>
        </span>
      ) : null}
    </span>
  );
};

export default InfoTooltip;
